"use client";

import { useState, useEffect, useCallback } from "react";

interface ImageGalleryProps {
  images: string[];
}

export function ImageGallery({ images }: ImageGalleryProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const previousImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
          nextImage();
          break;
        case "ArrowLeft":
          previousImage();
          break;
        case "Escape":
          closeLightbox();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, nextImage, previousImage, closeLightbox]);

  if (!images || images.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {/* Show 4 images on mobile (2x2), 3 on larger screens */}
        {images.slice(0, 4).map((image, imgIndex) => (
          <div
            key={imgIndex}
            className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer group ${
              imgIndex === 3 ? "sm:hidden" : ""
            }`}
            onClick={() => openLightbox(imgIndex)}
          >
            <img
              src={image}
              alt={`Gallery image ${imgIndex + 1}`}
              className="object-cover group-hover:scale-105 
                        transition-transform duration-300 w-full h-full"
            />
            {/* Show overlay for 4th image on mobile, 3rd image on larger screens */}
            {imgIndex === 3 && images.length > 4 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center sm:hidden">
                <span className="text-white text-lg font-medium">
                  +{images.length - 4} more
                </span>
              </div>
            )}
            {imgIndex === 2 && images.length > 3 && (
              <div className="absolute inset-0 bg-black/50 items-center justify-center hidden sm:flex">
                <span className="text-white text-lg font-medium">
                  +{images.length - 3} more
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
            aria-label="Close lightbox"
          >
            ✕
          </button>
          <button
            onClick={previousImage}
            className="absolute left-4 text-white p-4 hover:bg-white/10 rounded-full"
            aria-label="Previous image"
          >
            ←
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 text-white p-4 hover:bg-white/10 rounded-full"
            aria-label="Next image"
          >
            →
          </button>
          <img
            src={images[currentImageIndex]}
            alt={`Gallery image ${currentImageIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          <div className="absolute bottom-4 text-white">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
}
